import React from 'react';
import CSVReader from 'react-csv-reader';
import { useSelector } from 'react-redux';
import saveAs from 'file-saver';
import Papa from 'papaparse';
import {
  Box,
  Switch,
  Backdrop,
  DotProgress,
  Typography,
  Button,
  Grid,
  color,
  FormDialog,
  MuiBox,
  Tooltip,
  FlexBox,
  IconButton,
  Icon
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { selectors as authSelectors } from 'store/auth';
import { selectors as clientsSelectors, Client } from 'store/clients';
import { selectors as kiosksSelectors, actions as kiosksActions, Kiosk, KioskScheduleEntry } from 'store/kiosks';
import { KioskHardware } from 'store/kioskHardware';
import { tryToParseObject } from 'utils/stringFormat';
import packageJSON from '../../../package.json';
import KioskTable, { sort } from './components/KioskTable';
import { KioskFormOverlay } from './components/KioskFormOverlay';
import { useStyles } from './styled';
import { KioskFormData, Mode } from './types';

const KiosksView: React.FC = () => {
  const {
    getKiosks,
    resetKiosk,
    checkAppVersion,
    updateAutoRefresh,
    updateShowHiddenKiosks,
    updateKiosks,
    resetExportData,
    createKiosk,
    updateKiosk,
    deleteKiosk,
    resetAction
  } = useActions({
    getKiosks: kiosksActions.getKiosks,
    resetKiosk: kiosksActions.resetKiosk,
    checkAppVersion: kiosksActions.checkAppVersion,
    updateAutoRefresh: kiosksActions.updateAutoRefresh,
    updateShowHiddenKiosks: kiosksActions.updateShowHiddenKiosks,
    updateKiosks: kiosksActions.updateKiosks,
    resetExportData: kiosksActions.resetExportData,
    createKiosk: kiosksActions.createKiosk,
    updateKiosk: kiosksActions.updateKiosk,
    deleteKiosk: kiosksActions.deleteKiosk,
    resetAction: kiosksActions.resetAction
  });
  const admin = useSelector(authSelectors.selectAdmin);
  const isGlobalAdmin = admin?.role === 'GlobalAdmin';
  const client = useSelector(clientsSelectors.selectClient) as Client;
  const kiosks = useSelector(kiosksSelectors.selectKiosks);
  const appVersion = useSelector(kiosksSelectors.selectAppVersion);
  const autoRefresh = useSelector(kiosksSelectors.selectAutoRefresh);
  const showHiddenKiosks = useSelector(kiosksSelectors.selectShowHiddenKiosks);
  const message = useSelector(kiosksSelectors.selectMessage);
  const exportData = useSelector(kiosksSelectors.selectExportData);
  const exportPending = useSelector(kiosksSelectors.selectExportPending);
  const {
    error: kiosksError,
    pending: kiosksPending,
    success: kiosksSuccess
  } = useSelector(kiosksSelectors.selectAction);
  const stockCountsByPrinter = useSelector(kiosksSelectors.selectStockCountsByPrinter);

  const [sortParams, setSortParams] = React.useState({ column: 'name', order: 'asc' });
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedKiosk, setSelectedKiosk] = React.useState<Kiosk | null>(null);
  const [mode, setMode] = React.useState<Mode>('create');
  const [showDeleteOverlay, setShowDeleteOverlay] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const classes = useStyles()();

  // ON UPDATE OF CLIENT
  React.useEffect(() => {
    resetKiosk();
    checkAppVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  // ON VERSION CHANGE: RELOAD PAGE
  React.useEffect(() => {
    if (packageJSON.version && appVersion && packageJSON.version !== appVersion) {
      window.location.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appVersion]);

  // ON SUCCESS
  React.useEffect(() => {
    if (kiosksSuccess) {
      setSelectedKiosk(null);
      setIsOpen(false);
      resetAction();
    }
  }, [kiosksSuccess]);

  // ON ERROR
  React.useEffect(() => {
    if (kiosksError) {
      setErrorMessage(kiosksError);
      resetAction();
    }
  }, [kiosksError]);

  // ON EXPORT DATA FETCHED: WRITE TO CSV
  React.useEffect(() => {
    if (exportData && exportData.length > 0) {
      const kiosksToShow = exportData.filter((k: Kiosk) => k.configuration && k.configuration.showInKCC);
      const sortedData = sort(kiosksToShow, stockCountsByPrinter, sortParams.column, sortParams.order);
      const formattedData = sortedData.map((k: Kiosk) => {
        const extractedSchedule =
          k.schedule &&
          k.schedule.map((entry: KioskScheduleEntry) => ({
            day: entry.day,
            startTime: entry.startTime,
            endTime: entry.endTime
          }));
        const formattedSchedule = JSON.stringify(extractedSchedule);

        // Add stock counts of printers, depending on the includeInKiosksExport flag
        const printerStockCountsForKiosk = [...stockCountsByPrinter.keys()].reduce((acc, printerName) => {
          const kioskPrinter = k.hardware
            .filter((h) => h.type === 'printer')
            .find((h) =>
              printerName.length ? h.configuration.displayName === printerName : !h.configuration.displayName
            );
          if (kioskPrinter && kioskPrinter.configuration.includeInKiosksExport) {
            return {
              ...acc,
              [`${printerName} Remaining`.trim()]: stockCountsByPrinter.get(printerName)?.[k.id] || ''
            };
          }
          return acc;
        }, {});

        return {
          'Kiosk ID': k.id,
          'Kiosk Name': k.name,
          ...printerStockCountsForKiosk,
          Schedule: formattedSchedule
        };
      });

      const csvData = Papa.unparse(formattedData);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, 'Kiosks.csv');

      resetExportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  // UPDATE KIOSKS USING IMPORTED FILE
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFileUpload = (data: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const importedData = data.map((item: any) => {
      const kioskId = item['Kiosk ID'];

      // Add updated stock counts to the kioskHardware of the payload
      const kioskPrinters = (kiosks.find((k) => k.id === kioskId)?.hardware || []).filter((h) => h.type === 'printer');
      const updatedKioskHardware: Partial<KioskHardware>[] = [];
      [...stockCountsByPrinter.keys()].forEach((printerName) => {
        const kioskPrinter = kioskPrinters.find((kp) =>
          printerName ? kp.configuration.displayName === printerName : !kp.configuration.displayName
        );
        const updatedCurrentStock = item[`${printerName} Remaining`.trim()];
        if (kioskPrinter && updatedCurrentStock) {
          updatedKioskHardware.push({
            id: kioskPrinter.id,
            values: { currentStock: Number(updatedCurrentStock) }
          });
        }
      });

      // verify the schedule is in JSON format
      const scheduleArray = tryToParseObject(item.Schedule);
      // if not, setting to null will cause the API to fail
      if (!scheduleArray) return null;

      return {
        kioskId,
        kioskName: item['Kiosk Name'],
        hardware: updatedKioskHardware,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        schedule: scheduleArray.map((obj: any) => ({
          day: obj.day,
          startTime: obj.startTime,
          endTime: obj.endTime
        }))
      };
    });
    // call API to update all kiosks
    updateKiosks(importedData);
    // reset the imported data
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // OPEN FILE EXPLORER
  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleEditKiosk = (kiosk: Kiosk) => {
    setSelectedKiosk(kiosk);
    setMode('edit');
    setIsOpen(true);
  };

  const handleDeleteKiosk = (kiosk: Kiosk) => {
    setSelectedKiosk(kiosk);
    setShowDeleteOverlay(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedKiosk) return;

    await deleteKiosk({ id: selectedKiosk.id });
    // only close if successful
    setShowDeleteOverlay(false);
    setSelectedKiosk(null);
  };

  const handleCreateClick = () => {
    setSelectedKiosk(null);
    setMode('create');
    setIsOpen(true);
  };

  const handleCreateEditKiosk = (payload: KioskFormData) => {
    if (mode === 'create') {
      handleCreateKiosk(payload);
    } else if (mode === 'edit') {
      handleUpdateKiosk(payload);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleCreateKiosk = async (data: KioskFormData) => {
    const kioskPayload = {
      name: data.name,
      serialNumber: data.serialNumber.trim(),
      macAddress: data.macAddress.trim(),
      areaId: data.area,
      configurationId: data.configurationId,
      clientIntegrationId: data.clientIntegrationId,
      configuration: data.configuration,
      ticketTemplates: data.templateId.map((id) => ({
        ticketTemplateId: id
      })),
      hardware: data.hardware?.map((h) => ({
        hardwareId: h.hardwareId,
        serialCode: h.serialCode,
        configuration: typeof h.configuration === 'string' ? JSON.parse(h.configuration) : h.configuration || {},
        values: typeof h.values === 'string' ? JSON.parse(h.values) : h.values || {}
      }))
    };

    await createKiosk(kioskPayload);
    setTimeout(() => {
      getKiosks({});
    }, 500);
  };

  const handleUpdateKiosk = async (data: KioskFormData) => {
    const kioskPayload = {
      id: selectedKiosk?.id || null,
      name: data.name.trim(),
      serialNumber: data.serialNumber.trim(),
      macAddress: data.macAddress.trim(),
      areaId: data.area,
      configurationId: data.configurationId,
      clientIntegrationId: data.clientIntegrationId,
      configuration: data.configuration,
      ticketTemplates: data.templateId.map((id) => ({
        ticketTemplateId: id
      })),
      hardware: data.hardware?.map((h) => ({
        id: h.id,
        hardwareId: h.hardwareId,
        serialCode: h.serialCode,
        configuration: typeof h.configuration === 'string' ? JSON.parse(h.configuration) : h.configuration || {},
        values: typeof h.values === 'string' ? JSON.parse(h.values) : h.values || {}
      }))
    };

    await updateKiosk(kioskPayload);
  };

  return (
    <MuiBox display="flex" flexDirection="column" height="100%" paddingBottom="24px">
      {kiosksPending && (
        <Backdrop sx={{ zIndex: '2147483647', opacity: '0.3' }} open={kiosksPending ? true : false}>
          <DotProgress color={color.LIGHT_GRAY} />
        </Backdrop>
      )}
      {showDeleteOverlay && selectedKiosk && (
        <FormDialog
          open={showDeleteOverlay}
          confirmButtonAction={handleDeleteConfirm}
          confirmButtonText="Delete"
          cancelButtonAction={() => {
            setShowDeleteOverlay(false);
          }}
          cancelButtonText="Cancel"
        >
          <MuiBox>
            <Typography fontSize={24}>Are you sure you want to delete this kiosk?</Typography>
          </MuiBox>
        </FormDialog>
      )}
      {/* HEADER ROW */}
      <Grid className={classes.kiosksHeaderBlock}>
        <Box flexOverride="none">
          <Typography className={classes.kiosksHeading}>Kiosks</Typography>
        </Box>
        <Box flexOverride="none">
          <Grid className={classes.switchContainer}>
            <Typography className={classes.switchLabel}>Auto Refresh</Typography>
            <Switch
              checked={autoRefresh}
              onChange={(_e, checked) => updateAutoRefresh(checked)}
              data-testid="kiosk-autorefresh-switch"
              sx={{ marginRight: '-16px' }}
            />
          </Grid>
          {isGlobalAdmin && (
            <Grid className={classes.switchContainer}>
              <Typography className={classes.switchLabel}>Show Hidden Kiosks</Typography>
              <Switch
                checked={showHiddenKiosks}
                onChange={(_e, checked) => updateShowHiddenKiosks(checked)}
                data-testid="show-hidden-kiosks-switch"
                sx={{ marginRight: '-16px' }}
              />
            </Grid>
          )}
        </Box>
      </Grid>
      <div className={classes.kioskButtonWrap}>
        {/* IMPORT BUTTON */}
        <Grid className={classes.tabButtonsWrap}>
          <Tooltip title="Import from .csv" placement="top" arrow verticalOffset={-8}>
            <Grid>
              <FlexBox direction="column">
                <IconButton
                  onClick={handleClick}
                  data-testid="kiosk-import-button"
                  disabled={kiosks.length <= 0}
                  style={{
                    backgroundColor: '#F46D2E',
                    color: 'white'
                  }}
                >
                  <Icon name="Publish" />
                  <CSVReader
                    ref={fileInputRef}
                    onFileLoaded={handleFileUpload}
                    parserOptions={{
                      header: true,
                      skipEmptyLines: true
                    }}
                    inputStyle={{ display: 'none' }}
                  />
                </IconButton>
              </FlexBox>
            </Grid>
          </Tooltip>
          {/* EXPORT BUTTON */}
          <Tooltip title="Export to .csv" placement="top" arrow verticalOffset={-8}>
            <Grid>
              <FlexBox direction="column">
                <IconButton
                  onClick={() => getKiosks({ exportData: true })}
                  disabled={kiosks.length <= 0}
                  style={{
                    backgroundColor: '#F46D2E',
                    color: 'white'
                  }}
                  data-testid="export-button"
                >
                  {!exportPending ? <Icon name="FileDownloadOutlined" /> : <DotProgress size={3} />}
                </IconButton>
              </FlexBox>
            </Grid>
          </Tooltip>
          {isGlobalAdmin && (
            <Grid>
              <Button
                customSize="small"
                width={200}
                onClick={() => handleCreateClick()}
                data-testid="create-kiosk-button"
              >
                Create Kiosk
              </Button>
            </Grid>
          )}
        </Grid>
        {/* AUTO REFRESH TOGGLE */}
      </div>
      {/* TABLE OF KIOSK RECORDS */}
      <KioskTable
        pending={kiosksPending}
        sortParams={sortParams}
        setSortParams={setSortParams}
        message={message}
        onEdit={handleEditKiosk}
        onDelete={handleDeleteKiosk}
        showHiddenKiosks={showHiddenKiosks}
      />
      {isOpen && (
        <KioskFormOverlay
          mode={mode}
          open={isOpen}
          initialData={selectedKiosk}
          onClose={() => {
            setIsOpen(false);
          }}
          onSuccess={handleCreateEditKiosk}
        />
      )}

      {/* ERROR OVERLAY */}
      {errorMessage && (
        <FormDialog
          cancelButtonAction={() => setErrorMessage(null)}
          confirmButtonAction={() => setErrorMessage(null)}
          confirmButtonText="Ok"
          open
        >
          <Typography fontSize={24} maxWidth="524px">
            {errorMessage}
          </Typography>
        </FormDialog>
      )}
    </MuiBox>
  );
};

export default KiosksView;
